/**
 * Helpdesk view models served by emr-api's /helpdesk endpoints.
 *
 * These are the EXACT response shapes the admin UI consumes — keep them in
 * sync with apps/admin-web/lib/helpdesk/types.ts (the contract consumer).
 * Dates are ISO strings; ids are strings (`id`, not `_id`).
 */

/** Canonical helpdesk lifecycle — shared collection contract with chat-api. */
export type HelpdeskTicketStatus = 'open' | 'pending' | 'snoozed' | 'solved' | 'closed';
/** Live-chat realtime sub-state (`live_chat` channel only). */
export type HelpdeskTicketLiveState = 'waiting' | 'active' | 'closed' | 'timed_out';
export type HelpdeskTicketChannel =
  | 'live_chat'
  | 'email'
  | 'sms'
  | 'whatsapp'
  | 'web'
  | 'manual';
export type HelpdeskTicketPriority = 'low' | 'normal' | 'high' | 'urgent';
/** Where a transcript message originated. */
export type HelpdeskTicketMessageSource = 'patient' | 'agent_ops' | 'agent_gorgias';

export interface TicketListItemVM {
  id: string;
  /** Gorgias ticket id — the number agents know the ticket by. */
  gorgiasTicketId?: number;
  subject: string;
  customerId?: string;
  customerName: string;
  customerEmail: string;
  organization: string;
  status: HelpdeskTicketStatus;
  liveState?: HelpdeskTicketLiveState;
  channel: HelpdeskTicketChannel;
  priority: HelpdeskTicketPriority;
  tags: string[];
  assigneeId?: string;
  assigneeName?: string;
  /** Snippet of the newest transcript message. */
  preview: string;
  previewFromAgent: boolean;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TicketMessageVM {
  id: string;
  fromAgent: boolean;
  body: string;
  bodyHtml?: string;
  authorName?: string;
  source: HelpdeskTicketMessageSource;
  createdAt: string;
}

export interface TicketNoteVM {
  id: string;
  content: string;
  createdBy: { id: string; fullName: string };
  createdAt: string;
}

export interface HelpdeskOrderVM {
  id: string;
  num: string;
  status: string;
  itemSummary: string;
  products?: string[];
  totalAmount: number;
  createdAt: string;
}

export interface HelpdeskSubscriptionVM {
  id: string;
  product: string;
  status: string;
  nextCycleDate?: string;
}

export interface PriorTicketVM {
  id: string;
  subject: string;
  status: HelpdeskTicketStatus;
  channel: HelpdeskTicketChannel;
  createdAt: string;
}

/** Customer-360 context shown beside a ticket or conversation. */
export interface HelpdeskCustomerVM {
  id?: string;
  name: string;
  email: string;
  phone?: string;
  organization: string;
  location?: string;
  memberSince?: string;
  orders: HelpdeskOrderVM[];
  subscriptions: HelpdeskSubscriptionVM[];
  priorTickets: PriorTicketVM[];
}

export interface TicketDetailVM {
  ticket: TicketListItemVM;
  messages: TicketMessageVM[];
  notes: TicketNoteVM[];
  customer: HelpdeskCustomerVM | null;
}

export interface HelpdeskTicketFilters {
  view: 'my-open' | 'unassigned' | 'all-open' | 'closed' | 'all';
  search?: string;
  channel?: HelpdeskTicketChannel;
  priority?: HelpdeskTicketPriority;
  limit?: number;
}

export interface HelpdeskTicketPatch {
  status?: HelpdeskTicketStatus;
  priority?: HelpdeskTicketPriority;
  /** `null` unassigns. */
  assigneeId?: string | null;
}

export interface HelpdeskAgentVM {
  id: string;
  name: string;
  email: string;
  initials: string;
}

/** Canned response. `body` may carry `{{firstName}}`-style tokens. */
export interface MacroVM {
  id: string;
  name: string;
  category?: string;
  body: string;
}

/** Sidebar badge counts for every view. */
export interface HelpdeskCounts {
  myOpen: number;
  unassigned: number;
  allOpen: number;
  inboxUnread: number;
  inboxUrgent: number;
  inboxFlagged: number;
}

/* ----------------------------- medical inbox ---------------------------- */

export type InboxChannel = 'EMAIL' | 'SMS' | 'WHATSAPP' | 'WEB';

/**
 * One patient's ongoing medical chat. There is no conversation entity on the
 * server — this is the per-customer grouping of the `messages` collection.
 */
export interface ConversationListItemVM {
  customerId: string;
  customerName: string;
  customerEmail: string;
  organization: string;
  preview: string;
  previewInbound: boolean;
  lastChannel: InboxChannel;
  lastActivity: string;
  messageCount: number;
  unreadCount: number;
  flagged: boolean;
  urgent: boolean;
  triageIntent?: string;
  triageTeam?: string;
}

export interface InboxMessageVM {
  id: string;
  inbound: boolean;
  channel: InboxChannel;
  content: string;
  subject?: string;
  adminUserName?: string;
  status: 'pending' | 'delivered' | 'read';
  createdAt: string;
}

export interface ConversationDetailVM {
  customer: HelpdeskCustomerVM | null;
  messages: InboxMessageVM[];
  /** Latest AI triage of the thread, when one exists. */
  triage?: {
    intent: string;
    summary: string;
    suggestedTeam: string;
    requiresUrgentAttention: boolean;
    createdAt: string;
  };
  flagged: boolean;
}

export interface HelpdeskInboxFilters {
  search?: string;
  channel?: InboxChannel;
  unreadOnly?: boolean;
  urgentOnly?: boolean;
  flaggedOnly?: boolean;
}
